import {CommonModule} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';

import {MaterialModule} from '../../../material.module';
import {BrregCompany} from '../../../models/company.model';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ClientService} from '../../../services/auth/client.service';
import {CompanyService} from '../../../services/enrich/company.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';

@Component({
  selector: 'app-enrich-company-detail',
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-detail.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  imports: [CommonModule, MaterialModule, BreadcrumbsComponent]
})
export class EnrichCompanyDetailComponent implements OnInit {
  private companyService = inject(CompanyService);
  private clientService = inject(ClientService);
  private clientLoadingService = inject(ClientLoadingService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

  company: BrregCompany | null = null;
  isLoading = false;
  error: string | null = null;

  ngOnInit(): void {
    const orgNumber = this.route.snapshot.paramMap.get('orgNumber');
    if (!orgNumber) {
      this.error = 'Missing organization number';
      return;
    }

    const navigationState = this.router.getCurrentNavigation()?.extras.state as {company?: unknown} | undefined;
    const historyState = history.state as {company?: unknown} | undefined;
    const stateValue = navigationState?.company ?? historyState?.company;
    const stateId = getCompanyId(stateValue);
    if (stateId && stateId === orgNumber) {
      this.company = normalizeCompany(stateValue, orgNumber);
    }

    this.error = null;

    this.clientLoadingService.ensureClientSelected().subscribe({
      next: () => {
        this.loadCompany(orgNumber, {silent: Boolean(this.company)});
      },
      error: err => {
        this.enrichDiagnostics.reportError('enrich.company-detail.client-selection', err);
        if (!this.company) {
          this.error = 'No client selected. Please select a client from the dropdown.';
          this.isLoading = false;
        }
      }
    });

    this.isLoading = !this.company;
  }

  get breadcrumbs(): Array<{label: string; route?: string}> {
    return [
      {label: 'Shine Enrich', route: '/enrich/setup'},
      {label: 'Company data', route: '/enrich/companies'},
      {label: this.company ? this.company.company_name : 'Company details'}
    ];
  }

  goBack(): void {
    this.router.navigate(['/enrich/companies'], {queryParamsHandling: 'preserve'});
  }

  websiteHref(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
  }

  private loadCompany(orgNumber: string, options: {silent?: boolean} = {}): void {
    const silent = options.silent === true;

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      if (!this.company) {
        this.error = 'No client selected';
        this.isLoading = false;
      }
      return;
    }

    if (!silent) {
      this.isLoading = true;
    }

    this.companyService
      .getCompanyByOrgNumber(orgNumber, clientId)
      .pipe(
        catchError(error => {
          this.enrichDiagnostics.reportError('enrich.company-detail.load', error);
          if (!this.company) {
            this.error = 'Failed to load company';
          }
          return of(null as BrregCompany | null);
        }),
        finalize(() => {
          if (!silent) {
            this.isLoading = false;
          }
        })
      )
      .subscribe(company => {
        if (!company) {
          if (!this.company) {
            this.error = 'Company not found';
          }
          return;
        }

        this.company = company;
      });
  }
}

function getCompanyId(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const id = (value as any).company_id as unknown;
  if (typeof id === 'string' && id.length > 0) return id;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  return null;
}

function normalizeCompany(value: unknown, companyId: string): BrregCompany {
  if (!value || typeof value !== 'object') {
    return {company_id: companyId, company_name: companyId};
  }
  return {...(value as any), company_id: companyId} as BrregCompany;
}
